import { AUTH_PAGES_SWITCH } from '@/utils/authPages.js'

const UNAUTHENTICATED_SOFT_PLAY_LIMIT_ENABLED = Boolean(parseInt(process.env.VUE_APP_UNAUTHENTICATED_SOFT_PLAY_LIMIT_ENABLED))
const UNAUTHENTICATED_PLAY_LIMIT_ENABLED = Boolean(parseInt(process.env.VUE_APP_UNAUTHENTICATED_PLAY_LIMIT_ENABLED))
const UNAUTHENTICATED_SOFT_PLAY_LIMIT = parseInt(process.env.VUE_APP_UNAUTHENTICATED_SOFT_PLAY_LIMIT)
const UNAUTHENTICATED_PLAY_LIMIT = parseInt(process.env.VUE_APP_UNAUTHENTICATED_PLAY_LIMIT)

// initial state
const state = {
  trackList: [],
  currentTrackIndex: null,
  isPlaying: false,
  playProgress: 0, // Fraction (0-1) of the current track played, for mini-waveforms.
  playAfterLoad: false, // Used when loading a track
  playbackRequest: null, // 'play' or 'pause' for the loaded track, asked for from outside the player.
  unauthenticatedPlayCount: 0
}

// getters
const getters = {
  currentTrack: (state) => {
    if (state.currentTrackIndex !== null) {
      return state.trackList[state.currentTrackIndex]
    }
    return null
  },

  hasPrev: (state) => {
    return state.currentTrackIndex > 0
  },

  hasNext: (state) => {
    return state.currentTrackIndex < state.trackList.length - 1
  },

  playbackRequest: (state) => {
    return state.playbackRequest
  },

  isPlaying: (state) => {
    return state.isPlaying
  },

  playProgress: (state) => {
    return state.playProgress
  },

  unauthenticatedPlayCount: (state) => {
    return state.unauthenticatedPlayCount
  },

  isPlaybackSoftBlocked: (state, getters, rootState, rootGetters) => {
    // The full-page sign-up replaces the soft prompt, so there's none while it's on.
    if (rootGetters['waffle/isFeatureEnabled'](AUTH_PAGES_SWITCH)) {
      return false
    }
    // We use === not >= so that playback is only soft-blocked once.
    return !getters['auth/isLoggedIn'] && UNAUTHENTICATED_SOFT_PLAY_LIMIT_ENABLED && (state.unauthenticatedPlayCount === UNAUTHENTICATED_SOFT_PLAY_LIMIT)
  },

  isPlaybackBlocked: (state, getters) => {
    return !getters['auth/isLoggedIn'] && UNAUTHENTICATED_PLAY_LIMIT_ENABLED && (state.unauthenticatedPlayCount >= UNAUTHENTICATED_PLAY_LIMIT)
  }
}

// actions
const actions = {
  loadTracks: (context, tracks) => {
    // Quick sanity check. Would prefer it if this was never true.
    if (tracks.length < 1) {
      return
    }

    const newTrackList = []
    const currentTrack = context.getters.currentTrack

    // We don't want the current track to play twice. If the user clicks play on search page
    // then clicks through to track detail page then `tracks` will contain the current track
    // as the first time in the list.
    if (currentTrack !== null && currentTrack !== undefined && tracks.length > 0 && currentTrack.pk !== tracks[0].pk) {
      newTrackList.push(currentTrack)
    }

    newTrackList.push(...tracks)
    context.commit(CLEAR_TRACKS)
    context.commit(APPEND_TRACKS, newTrackList)

    if (currentTrack !== null) {
      // Whatever the current track was previously, it should occupy the first position
      // in the array so let's set the index accordingly.
      context.commit(SET_CURRENT_TRACK_INDEX, 0)
    }
  },

  prevTrack: (context) => {
    if (context.state.currentTrackIndex > 0) {
      if (!context.rootGetters['auth/isLoggedIn']) {
        context.commit(INCREMENT_UNAUTHENTICATED_PLAY_COUNT)
      }
      return context.commit(DECREMENT_TRACK)
    }
  },

  nextTrack: (context) => {
    if (context.state.currentTrackIndex < context.state.trackList.length - 1) {
      if (!context.rootGetters['auth/isLoggedIn']) {
        context.commit(INCREMENT_UNAUTHENTICATED_PLAY_COUNT)
      }
      return context.commit(INCREMENT_TRACK)
    }
  },

  setIsPlaying: (context, value) => {
    return context.commit(SET_IS_PLAYING, value)
  },

  setPlayProgress: (context, value) => {
    // Called every animation frame while playing; a mini-waveform is ~160px wide, so
    // only commit changes it could show.
    const rounded = Math.round(value * 1000) / 1000
    if (rounded !== context.state.playProgress) {
      context.commit(SET_PLAY_PROGRESS, rounded)
    }
  },

  setPlayAfterLoad: (context, value) => {
    return context.commit(SET_PLAY_AFTER_LOAD, value)
  },

  setPlaybackRequest: (context, value) => {
    return context.commit(SET_PLAYBACK_REQUEST, value)
  },

  pause: (context) => {
    return context.commit(SET_PLAYBACK_REQUEST, 'pause')
  },

  playTrack: (context, trackPk) => {
    const idx = context.state.trackList.findIndex(track => track.pk === trackPk)

    // Not in the queue (e.g. clicked before the page finished loading it). Setting the
    // index to -1 would leave the player on the old track with playAfterLoad stuck on,
    // so do nothing instead.
    if (idx === -1) {
      return
    }

    // Already loaded: the index won't change, so the player won't reload it. Ask it to
    // resume instead. This isn't a new play, so it doesn't count towards the play limit.
    if (idx === context.state.currentTrackIndex) {
      context.commit(SET_PLAYBACK_REQUEST, 'play')
      return
    }

    context.commit(SET_CURRENT_TRACK_INDEX, idx)
    // Instruct the player to play track after it has detected the current track has changed.
    context.commit(SET_PLAY_AFTER_LOAD, true)

    if (!context.rootGetters['auth/isLoggedIn']) {
      context.commit(INCREMENT_UNAUTHENTICATED_PLAY_COUNT)
    }
  },

  clearUnauthenticatedPlayCount: (context) => {
    return context.commit(CLEAR_UNAUTHENTICATED_PLAY_COUNT)
  }
}

// mutations
const CLEAR_TRACKS = 'CLEAR_TRACKS'
const APPEND_TRACKS = 'APPEND_TRACKS'
const INCREMENT_TRACK = 'INCREMENT_TRACK'
const DECREMENT_TRACK = 'DECREMENT_TRACK'
const SET_CURRENT_TRACK_INDEX = 'SET_CURRENT_TRACK_INDEX'
const SET_IS_PLAYING = 'SET_IS_PLAYING'
const SET_PLAY_PROGRESS = 'SET_PLAY_PROGRESS'
const SET_PLAY_AFTER_LOAD = 'SET_PLAY_AFTER_LOAD'
const SET_PLAYBACK_REQUEST = 'SET_PLAYBACK_REQUEST'
const INCREMENT_UNAUTHENTICATED_PLAY_COUNT = 'INCREMENT_UNAUTHENTICATED_PLAY_COUNT'
const CLEAR_UNAUTHENTICATED_PLAY_COUNT = 'CLEAR_UNAUTHENTICATED_PLAY_COUNT'

const mutations = {
  [CLEAR_TRACKS]: (state) => {
    state.trackList = []
  },

  [APPEND_TRACKS]: (state, tracks) => {
    state.trackList.push(...tracks)
  },

  [DECREMENT_TRACK]: (state) => {
    if (state.currentTrackIndex !== null) {
      state.currentTrackIndex -= 1
    } else {
      state.currentTrackIndex = 0
    }
  },

  [INCREMENT_TRACK]: (state) => {
    if (state.currentTrackIndex !== null) {
      state.currentTrackIndex += 1
    } else {
      state.currentTrackIndex = 0
    }
  },

  [SET_CURRENT_TRACK_INDEX]: (state, value) => {
    state.currentTrackIndex = value
  },

  [SET_IS_PLAYING]: (state, value) => {
    state.isPlaying = value
  },

  [SET_PLAY_PROGRESS]: (state, value) => {
    state.playProgress = value
  },

  [SET_PLAY_AFTER_LOAD]: (state, value) => {
    state.playAfterLoad = value
  },

  [SET_PLAYBACK_REQUEST]: (state, value) => {
    state.playbackRequest = value
  },

  [INCREMENT_UNAUTHENTICATED_PLAY_COUNT]: (state) => {
    if (state.unauthenticatedPlayCount !== null) {
      state.unauthenticatedPlayCount += 1
    } else {
      state.unauthenticatedPlayCount = 1
    }
  },

  [CLEAR_UNAUTHENTICATED_PLAY_COUNT]: (state) => {
    state.unauthenticatedPlayCount = 0
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
